import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  EmailTemplate,
  EmailTemplateMutateData,
  PersonalizationTokens,
  RenderedEmailTemplate
} from "../types/email-template.types.ts"

const BASE_URL = "/templates-management/email-templates"

const PATHS = {
  list: `${BASE_URL}/`,
  create: `${BASE_URL}/`,
  detail: (id: string) => `${BASE_URL}/${id}/`,
  uploadImage: `${BASE_URL}/images/`,
  tokens: `${BASE_URL}/tokens/`,
  render: (id: string) => `${BASE_URL}/${id}/render/`,
  send: (id: string) => `${BASE_URL}/${id}/send/`,
  sendToLead: `${BASE_URL}/send-to-lead/`
}

export const getEmailTemplates = (params?: { page_size?: number }) =>
  request.api.get<BasePagination<EmailTemplate>, BaseApiResponse<void>>(
    PATHS.list,
    params
  )

export const createEmailTemplate = (data: EmailTemplateMutateData) =>
  request.api.post<EmailTemplate, BaseApiResponse<void>>(PATHS.create, data)

export const updateEmailTemplate = (id: string, data: EmailTemplateMutateData) =>
  request.api.patch<EmailTemplate, BaseApiResponse<void>>(PATHS.detail(id), data)

export const deleteEmailTemplate = (id: string) =>
  request.api.delete<BaseApiResponse<null>>(PATHS.detail(id))

export const uploadEmailTemplateImage = (file: File) => {
  const formData = new FormData()
  formData.append("image", file)

  // A POST response is enveloped by the backend's EnvelopeJSONRenderer
  // (`{status, details: {..., data}}`) — unlike create/update above, this
  // response's payload is actually read on success (the uploaded url), so
  // the envelope has to be modeled here rather than silently ignored.
  return request.api.post<BaseApiResponse<{ url: string }>>(
    PATHS.uploadImage,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  )
}

// The automation's own personalization tokens, offered to template editors.
export const getEmailTemplateTokens = () =>
  request.api.get<PersonalizationTokens, BaseApiResponse<void>>(PATHS.tokens)

// The template with one lead's details filled in, as its customer would read it.
export const renderEmailTemplateForLead = (id: string, leadId: string) =>
  request.api.get<RenderedEmailTemplate, BaseApiResponse<void>>(PATHS.render(id), {
    lead: leadId
  })

export const sendEmailTemplateToLead = (id: string, leadId: string) =>
  request.api.post<BaseApiResponse<{ recipient: string; sent: boolean }>>(PATHS.send(id), {
    lead: leadId
  })

export interface LeadEmailData {
  lead: string
  subject: string
  body: string
  /** The template it started from, so the Activity Log names it. */
  template?: string
  /** More people on the To line, after the customer. */
  to?: string[]
  cc?: string[]
}

export const sendEmailToLead = (data: LeadEmailData) =>
  request.api.post<BaseApiResponse<{ recipient: string; sent: boolean }>>(
    PATHS.sendToLead,
    data
  )
